/**
 * HEAD-2 — CE COMPOSANT N'EST PLUS UN COMPOSANT PUBLIC, ET IL N'A PAS ÉTÉ
 * SUPPRIMÉ POUR AUTANT.
 *
 * Il était le sélecteur de langue du second en-tête public
 * (`components/layout/Header.tsx`, monté sur treize routes), face au
 * `PublicLanguageSwitcher` de la home : deux composants pour un geste. Ce lot
 * unifie l'en-tête sur celui de la home, donc `PublicLanguageSwitcher` est
 * désormais le SEUL sélecteur des surfaces publiques — barre, panneau mobile et
 * les deux pieds de page.
 *
 * Il reste un appelant, vérifié et non deviné : `components/settings/
 * settings-appearance-section.tsx`, dans le produit CONNECTÉ, où ce sélecteur
 * est une ligne de préférence dans un panneau de réglages et pas un contrôle de
 * chrome. Le retirer aurait demandé de re-dessiner cette ligne avec un composant
 * fait pour une barre de navigation — un item de l'OS, pas de l'en-tête public.
 * Il n'est donc PAS orphelin : ce fichier existe pour dire lequel des deux cas
 * est vrai, parce que la question se reposera.
 */
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';
import { applyLanguage } from '@/i18n/applyLanguage';

interface LanguageSwitcherProps {
  className?: string;
}

export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation('common');

  const current =
    SUPPORTED_LANGUAGES.find((l) => i18n.resolvedLanguage === l.code) ??
    SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) ??
    SUPPORTED_LANGUAGES[0]!;

  const changeLanguage = (code: string) => {
    applyLanguage(code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('actions.changeLanguage')}
        /* `min-h-11` — 44px. Mesuré (audit:responsive) : `px-2 py-1.5` autour
           d'une ligne de 20px donne 75×32, et c'est le sélecteur de langue de
           l'en-tête marketing, sur douze pages, à tous les viewports tactiles.
           Le pendant public de ce contrôle (`PublicLanguageSwitcher`) pose ce
           même plancher depuis CH-5 ; celui-ci ne l'avait jamais reçu. */
        className={cn(
          'inline-flex min-h-11 shrink-0 items-center gap-1 rounded-control border border-border bg-background px-2 py-1.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          className,
        )}
      >
        {/* Plus de drapeau : l'abréviation seule, en mono. Elle était déjà là,
            à droite de l'emoji — le déclencheur disait donc deux fois la même
            chose, une fois mal. */}
        <span className="font-data text-[11px] uppercase tracking-[0.14em]">{current.short}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {SUPPORTED_LANGUAGES.map((lang) => {
          const active = lang.code === current.code;
          return (
            <DropdownMenuItem
              key={lang.code}
              onSelect={() => changeLanguage(lang.code)}
              className={cn('flex items-center gap-2 cursor-pointer', active && 'font-semibold')}
            >
              <span className="w-7 shrink-0 font-data text-[10px] uppercase tracking-[0.14em] text-muted-foreground" aria-hidden="true">
                {lang.short}
              </span>
              <span className="flex-1">{lang.label}</span>
              {active && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
